// src/utils/searchCorrection.js
//
// The kiosk's half of the catalog typo correction (backend utils/kioskCatalogSearch.js).
//
// SEARCH. With CATALOG_TOKEN_SEARCH_ENABLED on, a first page that found nothing may be
// answered with the results for a corrected spelling, and the response says so:
//
//   searchCorrection: { from: 'pikahcu', to: 'pikachu' }
//
// The backend corrects the FIRST page only: an unscoped search can be a catalog scan,
// and correcting every page would repeat it per page. So the kiosk has two jobs:
//   - Say so. Without a line, a shopper who typed one thing sees cards named another
//     and cannot tell a correction from a wrong result.
//   - Ask for every later page with the corrected words. The typed words match
//     nothing, so page 2 of them is empty, and infinite scroll would stop after one
//     page of a result that `total` says is longer.
//
// SUGGEST. The dropdown's options are the server's answer for what was typed: names
// starting with it, then names holding every typed word in any order ("bolt lightning"
// suggests "Lightning Bolt"), then, when nothing matched and correction is on, the
// corrected words. MUI's default filter keeps only options that CONTAIN the whole
// input, so it hid the last two. `suggestionFilter` shows the server's answer as sent
// while the box holds the text it answered, and keeps the default filter for the moment
// the shopper types ahead of the debounced request, where it narrows the previous
// term's options exactly as before.
//
// Pure (no imports) so it can be unit tested on its own.

const cleanString = (value) => (typeof value === 'string' ? value.trim() : '');

/** Typed text compared the way the server compares it: trimmed, case-insensitive. */
const typedKey = (value) => cleanString(value).toLowerCase();

/**
 * The correction a search response carries, or null when there is none (the normal
 * case), it is malformed, or it corrected nothing. It comes off the network, so it is
 * read defensively and only ever rendered as plain text.
 *
 * @param {object} data a /products/:store/search response body
 * @returns {{ from: string, to: string } | null}
 */
export function readSearchCorrection(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const correction = data.searchCorrection;
    if (!correction || typeof correction !== 'object' || Array.isArray(correction)) return null;

    const to = cleanString(correction.to);
    if (!to) return null;
    const from = cleanString(correction.from);
    if (!from || typedKey(from) === typedKey(to)) return null;

    return { from, to };
}

/** The line shown above the results, or '' when there is no correction. */
export function formatSearchCorrection(correction) {
    if (!correction || !correction.to) return '';
    return `Showing results for "${correction.to}". No results for "${correction.from}".`;
}

/**
 * The words to ask for on `page` of the search for `q`. Page 1 is always asked as
 * typed; a later page of a search whose page 1 was corrected is asked with the
 * corrected words.
 *
 * @param {string|undefined} q the search as typed (the request's `q`)
 * @param {number} page
 * @param {{ from: string, to: string } | null} correction what page 1 of `q` answered with
 */
export function searchTermForPage(q, page, correction) {
    if (!(page > 1) || !correction) return q;
    return typedKey(q) === typedKey(correction.from) ? correction.to : q;
}

/**
 * The suggest box's `filterOptions`. Options fetched for exactly what is in the box are
 * shown as the server sent them; anything else in the box (typing ahead of the
 * debounce) is narrowed by `defaultFilter`, MUI's own.
 *
 * @param {(options: any[], state: { inputValue: string }) => any[]} defaultFilter
 * @param {string} suggestionsFor the text the current options were fetched for
 */
export function suggestionFilter(defaultFilter, suggestionsFor) {
    const key = typedKey(suggestionsFor);
    return (options, state) => (key && typedKey(state.inputValue) === key ? options : defaultFilter(options, state));
}
